import { forwardRef, useId, useRef, useState } from 'react';
import cx from 'classnames';
import InlinePicker from '../Popover/InlinePicker';
import './Select.css';

/**
 * QHS Select v4 — form-field trigger + Tier 2 Inline Picker dropdown.
 * -------------------------------------------------------------------
 * The dropdown uses the existing `InlinePicker` / `Popover` primitives
 * so the menu styling (container shadow, item hover/active states,
 * check mark on the selected row) is the exact `.pop` / `.pop-item`
 * design-system spec with no one-off `sx` overrides.
 *
 * Trigger remains a form-field-styled button so inputs and selects in
 * the same FieldGrid stay visually aligned.
 *
 * Public API is unchanged from v2/v3:
 *   <Select label="State" fullWidth options={STATES} value={v} onChange={e => ...} />
 *
 * `onChange` still fires with a synthetic `{ target: { value } }`
 * payload — existing callers using `e.target.value` keep working.
 */
const ChevronIcon = ({ open }) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    style={{ transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : 'none' }}
    aria-hidden='true'
  >
    <polyline points='6 9 12 15 18 9' />
  </svg>
);

const Select = forwardRef(function Select(
  {
    label,
    helper,
    error,
    success,
    required,
    fullWidth = false,
    size = 'md',
    options = [],
    placeholder,
    className,
    id,
    value,
    onChange,
    disabled,
    searchThreshold = 8,
    searchPlaceholder,
    multiple = false,
    onSearch,
    emptyText,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const wrapperClasses = cx(
    'qhs-select',
    `qhs-select--${size}`,
    fullWidth && 'qhs-select--full',
    error && 'qhs-select--error',
    success && 'qhs-select--success',
    open && 'qhs-select--open',
    className
  );
  const message = error || success || helper;
  const messageType = error ? 'error' : success ? 'success' : 'helper';

  // Flatten options so the trigger can resolve labels for grouped
  // option lists (e.g. clinical exam findings split into Nasal /
  // Eye / Dermatological). When an option carries its own
  // `options: [...]` array, expand its leaves and ignore the group
  // header itself for value lookup.
  const flatLeaves = [];
  options.forEach((o) => {
    if (o && Array.isArray(o.options)) {
      o.options.forEach((child) => flatLeaves.push(child));
    } else {
      flatLeaves.push(o);
    }
  });

  // Derive what the trigger shows. For `multiple`, join the matched
  // option labels with a comma; a placeholder takes over when empty.
  let displayLabel = '';
  let hasValue = false;
  if (multiple) {
    const arr = Array.isArray(value) ? value : [];
    hasValue = arr.length > 0;
    displayLabel = arr
      .map((v) => {
        const o = flatLeaves.find((opt) => (opt?.value ?? opt) === v);
        return o?.label ?? o ?? v;
      })
      .join(', ');
  } else {
    const selected = flatLeaves.find((o) => (o?.value ?? o) === value);
    hasValue = !!selected;
    displayLabel = selected?.label ?? selected ?? '';
  }

  const handleChange = (newValue) => {
    // Emit a synthetic event so existing callers using
    // `onChange={(e) => setFieldValue(..., e.target.value)}` keep
    // working after the rewrite from native <select> / MUI Select.
    // For `multiple`, `newValue` is already an array.
    onChange?.({ target: { value: newValue, name: rest?.name } });
  };

  return (
    <div className={wrapperClasses} ref={ref}>
      {label && (
        <label htmlFor={inputId} className='qhs-select__label'>
          {label}
          {required && (
            <span className='qhs-select__req' aria-hidden='true'>
              *
            </span>
          )}
        </label>
      )}

      <InlinePicker
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        options={options}
        onChange={handleChange}
        searchThreshold={searchThreshold}
        searchPlaceholder={searchPlaceholder}
        multiple={multiple}
        onSearch={onSearch}
        emptyText={emptyText}
        matchTriggerWidth
        trigger={
          <button
            ref={triggerRef}
            id={inputId}
            type='button'
            className='qhs-select__input qhs-select__trigger'
            onClick={() => {
              if (!disabled) setOpen((v) => !v);
            }}
            disabled={disabled}
            aria-invalid={!!error || undefined}
            aria-haspopup='listbox'
            aria-expanded={open}
            {...rest}
          >
            <span
              className={cx(
                'qhs-select__value',
                !hasValue && 'qhs-select__value--placeholder'
              )}
            >
              {hasValue ? displayLabel : placeholder || ''}
            </span>
            <span className='qhs-select__chevron' aria-hidden='true'>
              <ChevronIcon open={open} />
            </span>
          </button>
        }
      />

      {message && (
        <span className={`qhs-select__msg qhs-select__msg--${messageType}`}>
          {message}
        </span>
      )}
    </div>
  );
});

export default Select;
